import { Link } from 'react-router-dom';
import { Package, Settings, Wrench, Thermometer, ArrowRight, ShoppingBag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator, BreadcrumbPage } from '../components/ui/breadcrumb';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import SEO from '../components/SEO';

const FAQPage = () => {
  const faqSections = [
    {
      id: 'achat-spa',
      title: 'Achat de spa',
      icon: ShoppingBag,
      color: 'from-[#1E3A5F] to-[#2d5a8f]',
      bgColor: 'bg-blue-50',
      borderColor: 'border-[#1E3A5F]/20',
      questions: [
        {
          question: "Acheter un spa en ligne, est-ce vraiment fiable ?",
          answer: "Oui. Acheter un spa en ligne permet de comparer, de réfléchir sereinement et d'échanger avant décision. Chaque projet spa fait l'objet d'un accompagnement humain, avant et après l'achat."
        },
        {
          question: "Suis-je accompagné avant de choisir mon spa ?",
          answer: "Oui. Avant toute validation, nous échangeons sur votre projet afin de vous orienter vers un spa adapté à votre usage, votre espace et vos attentes."
        },
        {
          question: "Vais-je être livré avec un spa \"clé en main\" ?",
          answer: "Le spa est livré prêt à être installé. Selon votre localisation, une installation ou une mise en service peut être proposée en complément."
        },
        {
          question: "Comment sont sélectionnés les spas proposés sur le site ?",
          answer: "Les spas sont sélectionnés pour leur fiabilité, la qualité de leurs composants et leur durabilité dans le temps. Nous privilégions des modèles réparables et suivis, plutôt que des produits jetables."
        },
        {
          question: "Les pièces détachées sont-elles disponibles dans le temps ?",
          answer: "Oui. La disponibilité des pièces détachées est un critère essentiel dans la sélection des spas proposés."
        },
        {
          question: "Quelle est la durée de vie moyenne d'un spa ?",
          answer: "Un spa bien installé et entretenu peut fonctionner de nombreuses années. La durée de vie dépend du modèle, de l'usage, de l'installation et de l'entretien."
        },
        {
          question: "Livrez-vous les spas partout en France ?",
          answer: "Oui. Les spas peuvent être livrés partout en France métropolitaine."
        },
        {
          question: "Proposez-vous l'installation du spa ?",
          answer: "L'installation peut être proposée dans certaines zones géographiques. Dans tous les cas, nous vous fournissons les informations nécessaires pour une installation conforme."
        },
        {
          question: "Que se passe-t-il en cas de problème après l'achat ?",
          answer: "Nous restons disponibles après l'achat pour vous orienter. Les interventions techniques, réparations et maintenances sont assurées via notre structure spécialisée.",
          link: {
            url: "https://www.atlantiquespaservice.com",
            text: "atlantiquespaservice.com"
          }
        }
      ]
    },
    {
      id: 'commande-livraison',
      title: 'Commande & Livraison',
      icon: Package,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      questions: [
        {
          question: "Livrez-vous partout en France ?",
          answer: "Oui. Les pièces détachées, accessoires, produits d'entretien et pompes à chaleur sont livrés partout en France métropolitaine."
        },
        {
          question: "Quels sont les délais de livraison ?",
          answer: "Les délais varient selon les produits et leur disponibilité. Les délais estimés sont indiqués lors de la commande."
        },
        {
          question: "Peut-on commander directement en ligne ?",
          answer: "Oui. Les pièces détachées, accessoires, produits d'entretien et pompes à chaleur peuvent être commandés directement en ligne. Les spas font l'objet d'une demande de devis."
        },
        {
          question: "Les prix affichés sont-ils TTC ?",
          answer: "Oui. Tous les prix affichés sur le site sont exprimés en euros, toutes taxes comprises (TTC)."
        }
      ]
    },
    {
      id: 'produits-compatibilite',
      title: 'Produits & Compatibilité',
      icon: Settings,
      color: 'from-slate-600 to-slate-700',
      bgColor: 'bg-slate-50',
      borderColor: 'border-slate-200',
      questions: [
        {
          question: "Comment être sûr de la compatibilité d'une pièce avec mon spa ?",
          answer: "Il appartient au client de vérifier la compatibilité avant commande. En cas de doute, vous pouvez nous contacter via le formulaire de contact pour obtenir des informations générales."
        },
        {
          question: "Vérifiez-vous la compatibilité avant l'expédition ?",
          answer: "Non. Les commandes sont préparées sur la base des références choisies par le client. Nous vous recommandons de vérifier attentivement les caractéristiques techniques avant validation."
        },
        {
          question: "Puis-je retourner un produit si je me suis trompé ?",
          answer: "Les conditions de retour dépendent de l'état du produit et de la nature de l'erreur. Veuillez consulter nos conditions générales ou nous contacter avant tout retour."
        }
      ]
    },
    {
      id: 'installation-sav',
      title: 'Installation, Entretien & SAV',
      icon: Wrench,
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      questions: [
        {
          question: "Proposez-vous l'installation des produits ?",
          answer: "L'installation peut être proposée pour certains équipements, principalement dans nos zones d'intervention locales (44, 85, 56, 49). En dehors de ces zones, les produits sont livrés sans installation."
        },
        {
          question: "Comment puis-je obtenir des conseils personnalisés ?",
          answer: "Vous pouvez nous contacter par email à contact@atlantiquespaboutique.com ou via notre formulaire de contact. Nous répondons sous 24h. Pour les interventions, nous proposons des prestations d'entretien, de contrôle et d'intervention selon les besoins."
        },
        {
          question: "Intervenez-vous pour la réparation de spas ?",
          answer: "Oui. Les interventions de réparation, maintenance et remplacement de composants sont assurées via notre structure dédiée.",
          link: {
            url: "https://www.atlantiquespaservice.com",
            text: "atlantiquespaservice.com"
          }
        }
      ]
    },
    {
      id: 'pompes-chaleur',
      title: 'Pompes à chaleur pour spa',
      icon: Thermometer,
      color: 'from-emerald-500 to-emerald-600',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-200',
      questions: [
        {
          question: "Peut-on acheter une pompe à chaleur pour spa directement en ligne ?",
          answer: "Oui. Les pompes à chaleur pour spa peuvent être achetées directement sur notre site."
        },
        {
          question: "Proposez-vous une étude de compatibilité pour les pompes à chaleur ?",
          answer: "Oui. Nous proposons une étude personnalisée afin de vérifier la compatibilité de la pompe à chaleur avec votre spa (volume, configuration, emplacement, environnement). Cette étude est recommandée pour garantir les performances et la durabilité de l'installation."
        },
        {
          question: "L'étude est-elle obligatoire avant l'achat d'une pompe à chaleur ?",
          answer: "Non. L'achat peut être effectué directement. L'étude personnalisée est fortement conseillée, mais reste optionnelle et permet d'éviter toute incompatibilité ou sous-dimensionnement."
        }
      ]
    }
  ];

  // Flatten all questions for JSON-LD
  const allQuestions = faqSections.flatMap(section => section.questions);

  // BreadcrumbList JSON-LD
  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Accueil",
        "item": "https://atlantiquespaboutique.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "FAQ",
        "item": "https://atlantiquespaboutique.com/faq"
      }
    ]
  };

  // FAQPage JSON-LD
  const faqPageJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": allQuestions.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="faq-page">
      <SEO 
        title="FAQ Spa | Commande, livraison, compatibilité | Atlantique Spa Boutique"
        description="Questions fréquentes sur l'achat de spas, pièces détachées, accessoires et pompes à chaleur."
        canonical="/faq"
      />
      
      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Accueil</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>FAQ</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>

      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Questions fréquentes
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Commande, livraison, compatibilité, installation : trouvez rapidement les réponses à vos questions
          </p>
        </div>
      </div>

      {/* FAQ Sections */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-10">
          {faqSections.map((section) => (
            <div key={section.id} className="scroll-mt-24" id={section.id}>
              {/* Section Header */}
              <div className="flex items-center gap-3 mb-5">
                <div className={`w-10 h-10 bg-gradient-to-br ${section.color} rounded-lg flex items-center justify-center shadow-lg`}>
                  <section.icon className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl font-bold text-slate-900">{section.title}</h2>
              </div>

              {/* Questions Accordion */}
              <Accordion type="single" collapsible className="space-y-3">
                {section.questions.map((faq, index) => (
                  <AccordionItem 
                    key={`${section.id}-${index}`}
                    value={`${section.id}-${index}`}
                    className={`bg-white border ${section.borderColor} rounded-xl px-6 data-[state=open]:border-[#1E3A5F] data-[state=open]:shadow-md transition-all`}
                  >
                    <AccordionTrigger className="text-left hover:no-underline py-5 gap-4">
                      <span className="text-slate-900 font-medium pr-4 leading-relaxed">
                        {faq.question}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="pb-5">
                      <p className="text-slate-600 leading-relaxed">
                        {faq.answer}
                      </p>
                      {faq.link && (
                        <a 
                          href={faq.link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 mt-3 text-[#1E3A5F] hover:text-orange-500 font-medium transition-colors"
                        >
                          <ArrowRight className="w-4 h-4" />
                          {faq.link.text}
                        </a>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </div>

        {/* CTA discret */}
        <div className="mt-16 text-center">
          <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm">
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              Besoin d'aide ?
            </h3>
            <p className="text-slate-600 mb-6">
              Contactez-nous pour toute question complémentaire
            </p>
            <Link to="/contact">
              <Button className="bg-[#1E3A5F] hover:bg-[#152C4A]">
                Nous contacter
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify(breadcrumbJsonLd)
      }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify(faqPageJsonLd)
      }} />
    </div>
  );
};

export default FAQPage;
